import * as React from 'react';
import { useEffect, useCallback, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Occupation, Variable } from '@restworld/utility-types';
import { useTaxonomyService } from '@restworld/data-services';

import { WizardSelectField, FormLabelLabel, WizardMultiSelect } from '../';
import { useErrorMessage } from '../../wizard-application-wizard.utils';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';

const rootKey = 'research_info';
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IWizardThirteenthProps {}
const fieldIndex = 0;
export const WizardThirteenth: React.FunctionComponent<IWizardThirteenthProps> = (props) => {
  const { control, getValues } = useFormContext();
  const { fields, append } = useFieldArray({ control, name: rootKey });
  const { domainInterest, restaurantType, occupationOptions, shifts } = useStepOptions({ fieldIndex });
  const fieldEl = useMemo(
    () =>
      fields.map((field, fieldIndex) => {
        const domainInterestId: string | undefined = getValues(`research_info.${fieldIndex}.domain_interest`);
        return (
          <div key={field.id} className="flex flex-col gap-4">
            <DomainInterestField options={domainInterest} />
            {occupationOptions?.length ? (
              <OccupationComponent options={occupationOptions} domainInterestId={domainInterestId} />
            ) : null}
            <RestaurantType options={restaurantType} domainInterestId={domainInterestId} />
          </div>
        );
      }),
    [domainInterest, fields, getValues, occupationOptions, restaurantType]
  );
  useEffect(() => {
    if (!fields.length) append({});
  }, [append, fields.length]);
  return (
    <div className="flex flex-col gap-4">
      {fieldEl}
      <Shifts options={shifts} />
    </div>
  );
};
export default WizardThirteenth;
type PropsType = {
  domainInterestId?: string;
  options: Variable[];
};
const DomainInterestField = ({ options }: PropsType) => {
  const { t } = useTranslation();
  const id = `research_info.${fieldIndex}.domain_interest`;
  const errorMessage = useErrorMessage({
    rootKey,
    id: 'domain_interest',
    fieldIndex: 0,
  });
  return (
    <WizardSelectField
      options={options}
      placeholder={t('onboarding-page:choose_the_sector')}
      id={id}
      formLabel={t('common:sector')}
      rules={{ required: true }}
      errorMessage={errorMessage}
      testId="worker-sector-options"
      inputTestId="worker-sector-input"
    />
  );
};
const OccupationComponent = ({ options, domainInterestId }: PropsType) => {
  const { t } = useTranslation();
  const id = `research_info.${fieldIndex}.occupations`;
  const errorMessage = useErrorMessage({
    rootKey,
    id: 'occupations',
    fieldIndex: 0,
  });
  return (
    <WizardMultiSelect
      options={options}
      placeholder={t('onboarding-page:role')}
      id={id}
      key={id}
      formLabel={`${t('onboarding-page:choose_role')}*`}
      mainTitle={t('onboarding-page:choose_role')}
      disabled={!domainInterestId}
      rules={{ required: true }}
      errorMessage={errorMessage}
      testId="worker-role-options"
    />
  );
};
const RestaurantType = ({ options, domainInterestId }: PropsType) => {
  const { t } = useTranslation();

  const id = `research_info.${fieldIndex}.restaurant_types`;
  const errorMessage = useErrorMessage({
    rootKey,
    id: 'restaurant_types',
    fieldIndex: 0,
  });
  return (
    <WizardMultiSelect
      options={options}
      placeholder={t('onboarding-page:choose_type')}
      id={id}
      key={id}
      formLabel={<FormLabelLabel label={t('onboarding-page:type_of_venue')} isOptional />}
      mainTitle={t('onboarding-page:type_of_venue')}
      disabled={!domainInterestId}
      rules={{ required: false }}
      errorMessage={errorMessage}
      testId="work-premises-options"
    />
  );
};
const Shifts = ({ options }: PropsType) => {
  const { t } = useTranslation();

  const id = 'shifts';
  const errorMessage = useErrorMessage({
    rootKey: 'shifts',
  });
  return options?.length ? (
    <WizardMultiSelect
      options={options}
      id={id}
      key={id}
      formLabel={t('common:hours')}
      rules={{ required: false }}
      errorMessage={errorMessage}
      isModal={false}
      testId="worker-shift-options"
    />
  ) : null;
};
const useStepOptions = ({ fieldIndex }: { fieldIndex: number }) => {
  const { getValues } = useFormContext();
  const {
    fieldOptions: { domainInterest, shifts, restaurantType },
  } = useWorkerApplicationCxt();
  const [occupationOptions, setOccupationOptions] = useState<Occupation[]>([]);
  const domainInterestId = getValues(`research_info.${fieldIndex}.domain_interest`);
  const taxonomyService = useTaxonomyService();
  const occupationOptionsCp: Variable[] = useMemo(
    () =>
      occupationOptions
        .filter((v) => v.label_it)
        .map((v) => ({
          id: v.id.toString(),
          key: v.id.toString(),
          label: v.label_it.label,
          description: v.label_it.description,
        })),
    [occupationOptions]
  );
  const getOccupationOptions = useCallback(async () => {
    if (!domainInterestId) return;
    try {
      let domainInterestIdCp = '';
      const findIndex = domainInterest.findIndex((v) => v.id === domainInterestId);
      if (findIndex > -1) domainInterestIdCp = String(domainInterest[findIndex]['id']);
      const response = await taxonomyService.indexOccupations(domainInterestIdCp);
      setOccupationOptions(response.data);
    } catch (error) {
      console.error(`error fetching occupation`, error);
    }
  }, [domainInterestId, domainInterest, taxonomyService]);
  useEffect(() => {
    getOccupationOptions();
  }, [getOccupationOptions]);
  const returnV = useMemo(
    () => ({
      domainInterest,
      restaurantType,
      occupationOptions: occupationOptionsCp,
      shifts,
    }),
    [domainInterest, occupationOptionsCp, restaurantType, shifts]
  );
  return returnV;
};
